/**
 * /apps/:slug/health — calm-by-default status surface.
 *
 * One plain-English line about whether the app is working, the current
 * live + preview versions, and the last smoke-check result. Live error
 * counts from Workers Observability appear inline.
 */

import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client.js';
import { AppTabs } from '../components/AppTabs.js';
import { IconActivity, IconHeart } from '../components/Icons.js';
import { ManagerOnlyPage } from '../components/ManagerOnly.js';
import { StatusBadge } from '../components/StatusBadge.js';
import { Banner, Card, CardHeader, PageHeader, PageSkeleton } from '../components/ui.js';
import { relTime } from '../lib/format.js';
import { useAppPageWith } from '../lib/useAppPage.js';

interface HealthVersion {
  version: number;
  status: string;
  ai_summary: string | null;
  completed_at: number | null;
  smoke_check_status?: string | null;
  smoke_check_error?: string | null;
}

interface HealthResponse {
  slug: string;
  status: 'ok' | 'warning' | 'no_live_yet';
  live: HealthVersion | null;
  preview: HealthVersion | null;
  recent_errors_24h: number;
}

const STATUS_STYLE: Record<
  HealthResponse['status'],
  { lead: string; dot: string; text: string; bg: string }
> = {
  ok: { lead: 'Working', dot: 'bg-ok', text: 'text-ok', bg: 'bg-ok/10' },
  warning: { lead: 'Needs attention', dot: 'bg-warn', text: 'text-warn', bg: 'bg-warn/10' },
  no_live_yet: {
    lead: 'Not published yet',
    dot: 'bg-faint',
    text: 'text-muted',
    bg: 'bg-surface-2',
  },
};

// Same shape as STATUS_STYLE but parameterised by an error count instead
// of the health status, so the "errors in 24h" tile can reuse the
// dot/text/bg tokens directly.
const ERROR_COUNT_STYLE: Record<
  'ok' | 'muted' | 'warn',
  { dot: string; text: string; bg: string }
> = {
  ok: { dot: 'bg-ok', text: 'text-ok', bg: 'bg-ok/10' },
  muted: { dot: 'bg-faint', text: 'text-muted', bg: 'bg-surface-2' },
  warn: { dot: 'bg-warn', text: 'text-warn', bg: 'bg-warn/10' },
};

export function Health() {
  const { slug } = useParams();
  const {
    app,
    payload: health,
    loading,
    error,
  } = useAppPageWith(slug, (s) =>
    api.get<HealthResponse>(`/api/apps/${encodeURIComponent(s)}/health`),
  );

  if (loading) return <PageSkeleton rows={3} />;
  if (error) return <Banner tone="error">{error}</Banner>;
  if (!app || !health) return null;

  if (!app.is_manager) {
    return (
      <ManagerOnlyPage
        app={app}
        title="Health"
        description="A quick read on whether this app is working right now."
      />
    );
  }

  const tone = STATUS_STYLE[health.status];
  const errorTone =
    health.recent_errors_24h === 0 ? 'ok' : health.recent_errors_24h < 10 ? 'muted' : 'warn';
  const errorToneStyles = ERROR_COUNT_STYLE[errorTone];

  return (
    <div className="animate-fade-in">
      <PageHeader
        back={{ to: `/apps/${app.slug}`, label: app.name }}
        title="Health"
        description="A quick read on whether this app is working right now."
      />

      <div className="grid grid-cols-12 gap-6">
        <AppTabs
          slug={app.slug}
          isManager={app.is_manager}
          hasSchedules={app.has_schedules ?? false}
        />
        <main className="col-span-12 lg:col-span-9 xl:col-span-10 min-w-0 space-y-6">
          <Card className={`!border-line ${tone.bg}`}>
            <div className="p-6 flex items-start gap-4">
              <div
                className={`flex-none w-12 h-12 rounded-xl flex items-center justify-center ${tone.bg} ${tone.text}`}
              >
                <IconHeart size={22} />
              </div>
              <div className="min-w-0">
                <p className={`font-display text-3xl font-semibold tracking-tight ${tone.text}`}>
                  {tone.lead}
                </p>
                <p className="text-muted text-sm mt-2 max-w-2xl">
                  {health.status === 'ok' &&
                    `Live is on v${health.live!.version}. Last published ${relTime(health.live!.completed_at)}.`}
                  {health.status === 'warning' &&
                    `Live is on v${health.live!.version} but its current state is "${health.live!.status}". Check the activity log for details.`}
                  {health.status === 'no_live_yet' &&
                    "You haven't published this app yet. Iterate on preview, then ask your AI agent (like Claude Code) to publish."}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <CardHeader title="Versions" description="What's currently deployed." />
            <VersionRow env="Live" version={health.live} />
            <VersionRow env="Preview" version={health.preview} />
          </Card>

          <Card>
            <CardHeader title="Recent errors" description="Logs from Workers Observability." />
            <div className="px-5 py-5 flex items-end gap-4">
              <div
                className={`font-display text-5xl font-semibold leading-none tabular ${errorToneStyles.text}`}
              >
                {health.recent_errors_24h}
              </div>
              <p className="text-sm text-muted pb-1">errors in the last 24 hours</p>
            </div>
            {health.recent_errors_24h >= 10 && (
              <div className="px-5 pb-5">
                <Banner tone="warn">
                  Owners get an email alert if the rate exceeds 5 errors in any 15-minute window
                  (one email per hour per app).
                </Banner>
              </div>
            )}
          </Card>

          <p className="text-xs text-muted inline-flex items-center gap-1.5">
            <IconActivity size={14} />
            Want to see who did what?{' '}
            <Link to={`/apps/${app.slug}/activity`} className="underline hover:text-ink">
              Check the activity log.
            </Link>
          </p>
        </main>
      </div>
    </div>
  );
}

function VersionRow({ env, version }: { env: string; version: HealthVersion | null }) {
  if (!version) {
    return (
      <div className="px-5 py-4 text-sm flex items-center justify-between border-b border-line last:border-b-0">
        <span className="text-xs uppercase tracking-wider text-muted font-medium">{env}</span>
        <span className="text-faint text-sm">Never deployed.</span>
      </div>
    );
  }
  return (
    <div className="px-5 py-4 text-sm border-b border-line last:border-b-0">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs uppercase tracking-wider text-muted font-medium">{env}</span>
        <span className="flex items-center gap-2">
          <span className="font-semibold text-ink tabular">v{version.version}</span>
          <StatusBadge status={version.status} />
          <span className="text-xs text-faint">{relTime(version.completed_at)}</span>
        </span>
      </div>
      {version.ai_summary && (
        <p className="text-muted text-sm mt-1.5 truncate">{version.ai_summary}</p>
      )}
      {version.smoke_check_error && (
        <p className="text-danger text-xs mt-1.5">Smoke check: {version.smoke_check_error}</p>
      )}
    </div>
  );
}
